import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut, Mail, Phone, RefreshCw, ArrowLeft, Inbox, Activity, FileText, Save, Plus, Trash2, X, Undo2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ozy_admin_session";
const LEGACY_TOKEN_KEY = "ozy_admin_token"; // pre-signed-session cleanup

// Migration helpers — sessionStorage clears when the browser session closes,
// which is the required behaviour after the 2026-08-02 auth hardening.
function _clearLegacyTokens() {
  try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch (_e) { /* ignore */ }
  try { sessionStorage.removeItem(LEGACY_TOKEN_KEY); } catch (_e) { /* ignore */ }
}
function _getToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch (_e) { return ""; }
}
function _setToken(v) {
  try { sessionStorage.setItem(TOKEN_KEY, v); } catch (_e) { /* ignore */ }
}
function _removeToken() {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch (_e) { /* ignore */ }
}

function LoginPanel({ onLogin }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      _setToken(data.token);
      onLogin(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#050505]">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-8 space-y-5"
      >
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-widest mb-6">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          <h1 className="font-bebas text-4xl tracking-wide">Admin access</h1>
          <p className="text-zinc-500 text-sm mt-1">Enter your admin password to view leads.</p>
        </div>
        <Input
          data-testid="admin-password-input"
          type="password"
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
        />
        <Button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-button"
          className="w-full h-11 bg-[#E50914] hover:bg-[#F6121D] rounded-full font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

function LeadsPanel({ token, onLogout }) {
  const [tab, setTab] = useState("leads"); // 'leads' | 'bin' | 'sig' | 'posts'
  const [leads, setLeads] = useState([]);
  const [binLeads, setBinLeads] = useState([]);
  const [sigData, setSigData] = useState({ recent: [], by_person: [] });
  const [loading, setLoading] = useState(true);
  // ?ref=<8-char> from an owner SMS deep-link. Survives login (the URL is
  // never rewritten by the login modal) and pre-populates the search filter
  // once the operator is authenticated. Sanitised to hex + max 8 chars so the
  // parameter can never carry payloads or PII.
  const [searchParams] = useSearchParams();
  const initialRef = (searchParams.get("ref") || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 8).toLowerCase();
  const [filter, setFilter] = useState(initialRef);
  // Track delete in-flight to disable buttons per-row
  const [busyIds, setBusyIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "leads") {
        const { data } = await axios.get(`${API}/admin/leads`, { headers: { "x-admin-token": token } });
        setLeads(data);
      } else if (tab === "bin") {
        const { data } = await axios.get(`${API}/admin/leads?bin=1`, { headers: { "x-admin-token": token } });
        setBinLeads(data);
      } else if (tab === "sig") {
        const { data } = await axios.get(`${API}/admin/signature-opens`, { headers: { "x-admin-token": token } });
        setSigData(data);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, tab]);

  useEffect(() => { load(); }, [load]);

  // Escape clears the search box across the whole panel
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && filter) setFilter(""); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filter]);

  const matches = (l, q) => {
    if (!q) return true;
    const needle = q.toLowerCase().trim();
    // Include lead id (UUID) so partial-id searches like "c61f5d31" work.
    const haystack = [
      l.id, l.name, l.first_name, l.last_name, l.email, l.phone,
      l.loan_amount, l.description, l.loan_type, l.timeline,
      l.location, l.postcode, l.source_channel,
      l.attribution?.ozy_search_phrase, l.attribution?.finance_category, l.attribution?.utm_source, l.attribution?.utm_campaign,
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(needle);
  };

  const source = tab === "bin" ? binLeads : leads;
  const filtered = source.filter((l) => matches(l, filter));
  const hasQuery = filter.trim().length > 0;

  // -------- Soft-delete / Restore / Permanent-delete actions --------
  const setBusy = (id, on) => setBusyIds((prev) => {
    const next = new Set(prev); on ? next.add(id) : next.delete(id); return next;
  });

  const softDelete = async (l) => {
    const label = l.name || l.id?.slice(0, 8) || "lead";
    if (!window.confirm(`Move ${label} to Bin?\n\nYou can restore it from the Bin tab. It stops counting as an active submission.`)) return;
    setBusy(l.id, true);
    try {
      await axios.delete(`${API}/admin/leads/${l.id}`, { headers: { "x-admin-token": token } });
      toast.success("Moved to Bin.");
      // Optimistic update
      setLeads((xs) => xs.filter((x) => x.id !== l.id));
      setBinLeads((xs) => [{ ...l, deleted_at: new Date().toISOString() }, ...xs]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally { setBusy(l.id, false); }
  };

  const restore = async (l) => {
    setBusy(l.id, true);
    try {
      await axios.post(`${API}/admin/leads/${l.id}/restore`, {}, { headers: { "x-admin-token": token } });
      toast.success("Restored.");
      setBinLeads((xs) => xs.filter((x) => x.id !== l.id));
      const { deleted_at: _da, deleted_by: _db, ...rest } = l;
      setLeads((xs) => [rest, ...xs]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed.");
    } finally { setBusy(l.id, false); }
  };

  const permanentDelete = async (l) => {
    const label = l.name || l.id?.slice(0, 8) || "lead";
    if (!window.confirm(`Permanently delete ${label}?\n\nThis cannot be undone. All lead data and notification history will be removed.`)) return;
    // Second explicit confirmation for irreversible action
    if (!window.confirm(`Are you absolutely sure?\n\nType-check: this is a hard delete. Click OK only if you have exported anything you need.`)) return;
    setBusy(l.id, true);
    try {
      await axios.delete(`${API}/admin/leads/${l.id}/permanent`, { headers: { "x-admin-token": token } });
      toast.success("Permanently deleted.");
      setBinLeads((xs) => xs.filter((x) => x.id !== l.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Permanent delete failed.");
    } finally { setBusy(l.id, false); }
  };

  const emptyBin = async () => {
    if (binLeads.length === 0) return;
    if (!window.confirm(`Permanently delete ALL ${binLeads.length} leads in Bin?\n\nThis cannot be undone.`)) return;
    if (!window.confirm(`Final confirmation: permanently delete ${binLeads.length} leads?`)) return;
    setLoading(true);
    try {
      const { data } = await axios.delete(`${API}/admin/bin`, { headers: { "x-admin-token": token } });
      toast.success(`Emptied Bin (${data.deleted} removed).`);
      setBinLeads([]);
    } catch (e) {
      toast.error("Empty Bin failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 sm:p-10">
      <div className="max-w-[1600px] mx-auto space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-widest mb-3">
              <ArrowLeft className="w-3 h-3" /> Back to site
            </Link>
            <h1 className="font-bebas text-5xl tracking-tight">
              {tab === "leads" && "Leads"}
              {tab === "bin" && "Bin"}
              {tab === "sig" && "Signature Opens"}
              {tab === "posts" && "Insights"}
            </h1>
            <p className="text-zinc-500 text-sm mt-1" data-testid="leads-count-text">
              {tab === "leads" && `${leads.length} active ${leads.length === 1 ? "submission" : "submissions"}`}
              {tab === "bin" && `${binLeads.length} in Bin`}
              {tab === "sig" && `${sigData.recent.length} recent opens · ${sigData.by_person.reduce((s, p) => s + p.count, 0)} all-time`}
              {tab === "posts" && `Blog & insights`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(tab === "leads" || tab === "bin") && (
              <div className="relative">
                <Input
                  data-testid="leads-search-input"
                  placeholder="Search id, name, email, phone, type, location…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="bg-[#0a0a0a] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11 w-80 pr-10"
                />
                {filter && (
                  <button
                    type="button"
                    data-testid="leads-search-clear-button"
                    aria-label="Clear search"
                    onClick={() => setFilter("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            {tab === "bin" && binLeads.length > 0 && (
              <Button
                onClick={emptyBin}
                variant="outline"
                data-testid="empty-bin-button"
                className="bg-[#1a0507] border-[#E50914]/40 text-[#E50914] hover:bg-[#E50914] hover:text-white h-11"
              >
                <AlertTriangle className="w-4 h-4 mr-2" /> Empty Bin
              </Button>
            )}
            <Button
              onClick={load}
              variant="outline"
              data-testid="refresh-leads-button"
              className="bg-[#0a0a0a] border-zinc-800 text-white hover:bg-[#111] hover:text-white h-11"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              data-testid="admin-logout-button"
              className="bg-[#0a0a0a] border-zinc-800 text-white hover:bg-[#111] hover:text-white h-11"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="inline-flex rounded-full bg-[#0a0a0a] border border-zinc-800 p-1" data-testid="admin-tabs">
          <button
            onClick={() => setTab("leads")}
            data-testid="tab-leads"
            className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-medium transition-all ${tab === "leads" ? "bg-[#E50914] text-white" : "text-zinc-400 hover:text-white"}`}
          >
            <Inbox className="w-3.5 h-3.5" /> Leads
          </button>
          <button
            onClick={() => setTab("bin")}
            data-testid="tab-bin"
            className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-medium transition-all ${tab === "bin" ? "bg-[#E50914] text-white" : "text-zinc-400 hover:text-white"}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Bin
          </button>
          <button
            onClick={() => setTab("sig")}
            data-testid="tab-signatures"
            className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-medium transition-all ${tab === "sig" ? "bg-[#E50914] text-white" : "text-zinc-400 hover:text-white"}`}
          >
            <Activity className="w-3.5 h-3.5" /> Signature opens
          </button>
          <button
            onClick={() => setTab("posts")}
            data-testid="tab-posts"
            className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-medium transition-all ${tab === "posts" ? "bg-[#E50914] text-white" : "text-zinc-400 hover:text-white"}`}
          >
            <FileText className="w-3.5 h-3.5" /> Insights
          </button>
        </div>

        {tab === "posts" && <PostsPanel token={token} />}

        {tab === "sig" && (
          <SignatureOpens loading={loading} data={sigData} />
        )}

        {(tab === "leads" || tab === "bin") && (
        <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto scroll-elegant" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead className="bg-[#0c0c0c] border-b border-zinc-800">
                <tr className="text-zinc-500 uppercase tracking-wider text-xs">
                  <th className="px-4 py-4 text-left font-medium">{tab === "bin" ? "Deleted" : "Submitted"}</th>
                  <th className="px-4 py-4 text-left font-medium">Ref</th>
                  <th className="px-4 py-4 text-left font-medium">Name</th>
                  <th className="px-4 py-4 text-left font-medium">Contact</th>
                  <th className="px-4 py-4 text-left font-medium">Type</th>
                  <th className="px-4 py-4 text-left font-medium">Amount</th>
                  <th className="px-4 py-4 text-left font-medium">Location</th>
                  <th className="px-4 py-4 text-left font-medium">Source</th>
                  <th className="px-4 py-4 text-left font-medium">Requirements</th>
                  <th className="px-4 py-4 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="px-4 py-12 text-center text-zinc-500">
                    <Loader2 className="w-5 h-5 animate-spin inline-block" />
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-12 text-center text-zinc-500" data-testid="no-leads-row">
                    {hasQuery
                      ? "No matching lead. Try a different search or press Escape to clear."
                      : (tab === "bin"
                          ? "Bin is empty. Deleted leads will appear here."
                          : "No leads yet. Share your landing page and they'll appear here.")}
                  </td></tr>
                )}
                {!loading && filtered.map((l) => {
                  const busy = busyIds.has(l.id);
                  const ref = (l.id || "").slice(0, 8).toUpperCase();
                  return (
                    <tr key={l.id} className="border-b border-zinc-900 hover:bg-[#0c0c0c] transition-colors align-top">
                      <td className="px-4 py-4 text-zinc-500 whitespace-nowrap text-xs">
                        {tab === "bin"
                          ? (l.deleted_at ? new Date(l.deleted_at).toLocaleString() : "—")
                          : new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-mono text-[11px] text-zinc-500">{ref}</span>
                      </td>
                      <td className="px-4 py-4 font-medium whitespace-nowrap">{l.name}</td>
                      <td className="px-4 py-4">
                        <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-[#E50914] text-xs">
                          <Mail className="w-3 h-3" />{l.email}
                        </a>
                        <div>
                          <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#E50914] text-xs mt-0.5">
                            <Phone className="w-3 h-3" />{l.phone}
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {l.loan_type ? (
                          <span className="inline-flex items-center text-xs bg-[#1a0507] border border-[#E50914]/30 text-[#E50914] rounded-full px-2.5 py-1">
                            {l.loan_type}
                          </span>
                        ) : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="px-4 py-4 text-[#E50914] font-semibold whitespace-nowrap">{l.loan_amount}</td>
                      <td className="px-4 py-4 text-zinc-400 whitespace-nowrap text-xs">{l.location || <span className="text-zinc-600">—</span>}</td>
                      <td className="px-4 py-4 text-xs max-w-[220px]" data-testid={`lead-source-${l.id}`}>
                        <LeadSource lead={l} />
                      </td>
                      <td className="px-4 py-4 text-zinc-400 max-w-[280px]">
                        <span
                          className="line-clamp-2 text-xs cursor-help"
                          title={l.description || ""}
                          data-testid={`lead-requirements-${l.id}`}
                        >
                          {l.description || <span className="text-zinc-600 italic">—</span>}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {tab === "leads" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => softDelete(l)}
                            disabled={busy}
                            data-testid={`delete-lead-${l.id}`}
                            aria-label={`Move ${l.name || 'lead'} to Bin`}
                            className="h-8 w-8 p-0 text-zinc-500 hover:text-[#E50914] hover:bg-[#1a0507]"
                          >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        ) : (
                          <div className="inline-flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => restore(l)}
                              disabled={busy}
                              data-testid={`restore-lead-${l.id}`}
                              aria-label={`Restore ${l.name || 'lead'}`}
                              title="Restore"
                              className="h-8 w-8 p-0 text-zinc-500 hover:text-emerald-400 hover:bg-emerald-950/40"
                            >
                              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => permanentDelete(l)}
                              disabled={busy}
                              data-testid={`permanent-delete-lead-${l.id}`}
                              aria-label={`Permanently delete ${l.name || 'lead'}`}
                              title="Permanently delete"
                              className="h-8 w-8 p-0 text-zinc-500 hover:text-[#E50914] hover:bg-[#1a0507]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function SignatureOpens({ loading, data }) {
  return (
    <div className="space-y-6">
      {/* Per-person summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sig-summary">
        {data.by_person.length === 0 && !loading && (
          <div className="col-span-full bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-500">
            No signature opens yet. Once Mark or Sarah&apos;s emails are opened by recipients with images enabled, you&apos;ll see them here.
          </div>
        )}
        {data.by_person.map((p) => (
          <div key={p.person} className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500">{p.person}</div>
            <div className="font-bebas text-4xl tracking-tight text-white mt-1">{p.count}</div>
            <div className="text-[11px] text-zinc-600 mt-1">last: {p.last ? new Date(p.last).toLocaleString() : "—"}</div>
          </div>
        ))}
      </div>

      {/* Recent opens */}
      <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto scroll-elegant">
          <table className="w-full text-sm">
            <thead className="bg-[#0c0c0c] border-b border-zinc-800">
              <tr className="text-zinc-500 uppercase tracking-wider text-xs">
                <th className="px-5 py-4 text-left font-medium">Opened</th>
                <th className="px-5 py-4 text-left font-medium">Signature</th>
                <th className="px-5 py-4 text-left font-medium">Client (User-Agent)</th>
                <th className="px-5 py-4 text-left font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-zinc-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block" />
                </td></tr>
              )}
              {!loading && data.recent.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-zinc-500">No opens recorded yet.</td></tr>
              )}
              {!loading && data.recent.map((r, i) => (
                <tr key={i} className="border-b border-zinc-900 hover:bg-[#0c0c0c] transition-colors">
                  <td className="px-5 py-4 text-zinc-400 whitespace-nowrap">{new Date(r.ts).toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center text-xs bg-[#1a0507] border border-[#E50914]/30 text-[#E50914] rounded-full px-2.5 py-1">{r.person}</span>
                  </td>
                  <td className="px-5 py-4 text-zinc-400 max-w-md"><span className="line-clamp-1 text-xs">{r.ua || "—"}</span></td>
                  <td className="px-5 py-4 text-zinc-500 text-xs whitespace-nowrap">{r.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-zinc-600">
        Note: Gmail proxies images through its own servers, so the &quot;client&quot; and &quot;IP&quot; columns will often show Google. Opens are only counted when the recipient&apos;s mail client loads remote images (some users have this disabled).
      </p>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => {
    // On first mount, clear any legacy raw-password token that used to live
    // in localStorage before the 2026-08-02 signed-session-token migration.
    _clearLegacyTokens();
    return _getToken();
  });

  // Privacy: never record the authenticated admin area in PostHog session replay.
  useEffect(() => {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    try { ph?.stopSessionRecording?.(); } catch (_e) { /* ignore */ }
    return () => { try { ph?.startSessionRecording?.(); } catch (_e) { /* ignore */ } };
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    _removeToken();
    _clearLegacyTokens();
    setToken("");
  };

  return (
    <>
      {/* Defence-in-depth: tell every well-behaved crawler not to index any
          /admin route, even if robots.txt is ignored. */}
      <Helmet>
        <title>Admin · OzyLoans</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      {!token ? <LoginPanel onLogin={setToken} /> : <LeadsPanel token={token} onLogout={logout} />}
    </>
  );
}

function PostsPanel({ token }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const blank = { id: null, title: "", slug: "", excerpt: "", body_md: "", cover_alt: "", category: "Insight", keywords: [], published: false };

  const headers = { "x-admin-token": token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/posts`, { headers });
      setPosts(data || []);
    } catch (e) { toast.error("Failed to load posts."); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 80);

  const save = async () => {
    if (!edit.title || !edit.slug || !edit.body_md) return toast.error("Title, slug and body are required.");
    const payload = { ...edit, keywords: typeof edit.keywords === "string" ? edit.keywords.split(",").map((k) => k.trim()).filter(Boolean) : edit.keywords };
    try {
      if (edit.id) {
        await axios.put(`${API}/admin/posts/${edit.id}`, payload, { headers });
        toast.success("Post updated.");
      } else {
        await axios.post(`${API}/admin/posts`, payload, { headers });
        toast.success("Post created.");
      }
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try { await axios.delete(`${API}/admin/posts/${id}`, { headers }); toast.success("Deleted."); load(); }
    catch (e) { toast.error("Delete failed."); }
  };

  if (edit) {
    return (
      <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="post-editor">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{edit.id ? "Edit post" : "New post"}</h2>
          <Button variant="ghost" onClick={() => setEdit(null)} className="text-zinc-500 hover:text-white">Cancel</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Title</label>
            <Input data-testid="post-title-input" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value, slug: edit.slug || slugify(e.target.value) })}
              className="bg-[#111] border-zinc-800 text-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Slug (URL)</label>
            <Input data-testid="post-slug-input" value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: slugify(e.target.value) })}
              className="bg-[#111] border-zinc-800 text-white" placeholder="how-to-exit-a-private-lender" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Category</label>
            <Input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}
              className="bg-[#111] border-zinc-800 text-white" placeholder="Refinance · Developer · Commercial · Commentary" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Keywords (comma-separated)</label>
            <Input value={Array.isArray(edit.keywords) ? edit.keywords.join(", ") : edit.keywords}
              onChange={(e) => setEdit({ ...edit, keywords: e.target.value })}
              className="bg-[#111] border-zinc-800 text-white" placeholder="private lender exit, bridging take-out, refinance" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Excerpt (1–2 sentences, shows in cards + meta description)</label>
          <textarea value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })}
            className="w-full bg-[#111] border border-zinc-800 rounded-md text-white p-3 text-sm h-20 resize-y" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">
            Body (markdown supported: ## heading, ### subheading, **bold**, - list)
          </label>
          <textarea data-testid="post-body-input" value={edit.body_md} onChange={(e) => setEdit({ ...edit, body_md: e.target.value })}
            className="w-full bg-[#111] border border-zinc-800 rounded-md text-white p-3 text-sm font-mono h-96 resize-y" />
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })}
              data-testid="post-published-checkbox" className="accent-[#E50914] w-4 h-4" />
            Publish (visible on /insights)
          </label>
          <div className="flex gap-2">
            {edit.id && <Button onClick={() => remove(edit.id)} variant="outline" className="border-zinc-800 text-zinc-400 hover:text-[#E50914]"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>}
            <Button onClick={save} data-testid="post-save-button" className="bg-[#E50914] hover:bg-[#F6121D] text-white"><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-zinc-500 text-sm">{posts.length} post{posts.length === 1 ? "" : "s"} · {posts.filter((p) => p.published).length} published</p>
        <Button onClick={() => setEdit(blank)} data-testid="new-post-button" className="bg-[#E50914] hover:bg-[#F6121D] text-white"><Plus className="w-3.5 h-3.5 mr-1.5" /> New post</Button>
      </div>
      <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden">
        {loading && <div className="p-10 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>}
        {!loading && posts.length === 0 && <div className="p-10 text-center text-zinc-500">No posts yet — click <strong className="text-white">New post</strong> to write your first insight.</div>}
        {!loading && posts.map((p) => (
          <div key={p.id} className="border-b border-zinc-900 last:border-b-0 px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#0c0c0c]">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                <span className={p.published ? "text-[#E50914]" : "text-zinc-600"}>{p.published ? "● Published" : "○ Draft"}</span>
                <span>·</span><span>{p.category}</span>
              </div>
              <div className="font-medium text-white truncate">{p.title}</div>
              <div className="text-zinc-500 text-xs mt-0.5">/insights/{p.slug}</div>
            </div>
            <Button onClick={() => setEdit({ ...p })} variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-[#111] hover:text-white" data-testid={`edit-post-${p.slug}`}>Edit</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Phase-1 minimal attribution visibility: source channel + collapsible search/UTM context
function LeadSource({ lead }) {
  const a = lead.attribution || {};
  const channel = lead.source_channel || (lead.source === "chat" ? "ozy_chat" : "") || "—";
  const hasCtx = a.ozy_search_phrase || a.utm_source || a.referrer || a.landing_page || a.finance_category;
  return (
    <div>
      <span className="inline-flex items-center text-[11px] bg-[#111] border border-zinc-800 text-zinc-300 rounded-full px-2.5 py-1 font-mono" data-testid={`lead-source-channel-${lead.id}`}>
        {channel}
      </span>
      {a.ozy_search_phrase && (
        <p className="mt-1.5 text-zinc-400 line-clamp-2" title={a.ozy_search_phrase} data-testid={`lead-search-phrase-${lead.id}`}>
          <span className="text-zinc-600">Search:</span> {a.ozy_search_phrase}
        </p>
      )}
      {["human_review", "human_assessment"].includes(a.conversion_intent) && (
        <p className="mt-1.5 text-[11px] text-[#E50914]" data-testid={`lead-human-review-${lead.id}`}>Human Review Requested: Yes</p>
      )}
      {hasCtx && (
        <details className="mt-1.5">
          <summary className="cursor-pointer text-[11px] text-zinc-600 hover:text-zinc-400">Search context</summary>
          <dl className="mt-1.5 space-y-0.5 text-[11px] text-zinc-500">
            {a.finance_category && <div><dt className="inline text-zinc-600">Category: </dt><dd className="inline">{a.finance_category}</dd></div>}
            {a.funding_universe_category && <div><dt className="inline text-zinc-600">Funding universe: </dt><dd className="inline">{a.funding_universe_category}</dd></div>}
            {a.search_id && <div><dt className="inline text-zinc-600">Search ref: </dt><dd className="inline font-mono">{a.search_id.slice(0, 8)}</dd></div>}
            {a.landing_page && <div><dt className="inline text-zinc-600">Landing: </dt><dd className="inline break-all">{a.landing_page}</dd></div>}
            {a.referrer && <div><dt className="inline text-zinc-600">Referrer: </dt><dd className="inline break-all">{a.referrer}</dd></div>}
            {(a.utm_source || a.utm_medium || a.utm_campaign) && (
              <div><dt className="inline text-zinc-600">UTM: </dt><dd className="inline">{[a.utm_source, a.utm_medium, a.utm_campaign, a.utm_term, a.utm_content].filter(Boolean).join(" / ")}</dd></div>
            )}
            {a.device_type && <div><dt className="inline text-zinc-600">Device: </dt><dd className="inline">{a.device_type}</dd></div>}
          </dl>
        </details>
      )}
    </div>
  );
}
